import React, { useState } from "react";
import { Link } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { MailQuestion, ShieldAlert } from "lucide-react";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot", { identifier: identifier.trim() });
      setSubmitted(true);
      toast.success("RESET REQUEST SUBMITTED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-6 py-16" data-testid="forgot-page">
      <GlassPanel className="p-10 w-full max-w-md" scanline>
        <ShieldAlert size={32} className="text-cyan-400 mb-3" />
        <div className="gps-tag mb-3">RESET REQUEST</div>
        <h1 className="gps-heading text-3xl">FORGOT PASSWORD</h1>
        <p className="text-mute text-sm mt-2">
          An admin will issue a one-time temporary password and share it with you privately.
        </p>

        {submitted ? (
          <div
            className="mt-8 border border-cyan-400/30 rounded-lg p-4 bg-cyan-400/5"
            data-testid="forgot-success"
          >
            <div className="gps-mono text-cyan text-xs tracking-[0.2em]">
              <MailQuestion size={14} className="inline mr-1" /> REQUEST RECEIVED
            </div>
            <p className="text-mute text-sm mt-3 leading-relaxed">
              If an account matches, our admin team will contact you with a temporary password
              within 24 hours. Once you log in with it, you'll be prompted to set a new password.
            </p>
            <div className="mt-5 flex gap-2">
              <Link to="/login" className="gps-btn" data-testid="forgot-back-login">
                BACK TO LOGIN
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="gps-label">EMAIL OR PHONE</label>
              <input
                type="text"
                className="gps-input"
                required
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="athlete@gps.io OR +15551234567"
                data-testid="forgot-identifier"
              />
            </div>
            <button type="submit" disabled={busy} className="gps-btn w-full" data-testid="forgot-submit">
              {busy ? "SUBMITTING..." : "REQUEST RESET"}
            </button>
          </form>
        )}

        <div className="hud-divider my-6" />
        <div className="text-mute text-xs gps-mono text-center">
          REMEMBER IT? <Link to="/login" className="text-white" data-testid="forgot-to-login">LOGIN</Link>
        </div>
      </GlassPanel>
    </div>
  );
}
